// Finance build — the per-loan "Income events" list for the Income Overview
// drill. One row per recognized income event with its money split and the
// commissions it generated (claim number, role, amount, status). Read-only.

import {
  Card,
  EmptyState,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type StatusTone,
} from "@/design-system/components/ui";
import { FALLBACK_TIMEZONE, formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import type { LoanIncomeEventDto } from "@/api/portfolioAdmin/incomeOverviewApi";

/**
 * 🔴 PHOENIX ON PURPOSE — the one instant on the staff side that deliberately
 * does NOT follow the viewer, spelled out rather than omitted.
 *
 * `IncomeEvent.RecognizedAt` IS a genuine instant (`IncomeEvent.cs:80`
 * defaults it to `DateTimeOffset.UtcNow`, and `Core/Common/IncomePeriods.cs:6`
 * says so in as many words), so the reflex is to render it in the viewer's
 * zone like every other instant. That is wrong HERE, and only here, because of
 * what these rows sit inside: `IncomeOverviewService.ResolveWindow` buckets
 * income on the **America/Phoenix** day boundary — "the business is in
 * Arizona; 'June' means June there" — and `IncomePeriods.cs` records a FIXED
 * DEFECT in which "the window and its own rows disagreed".
 *
 * Localising the row label reopens exactly that: a Tokyo viewer with September
 * selected would see a row stamped Oct 1 for an event Phoenix booked on
 * Sep 30. The bucket is the authority, so the label follows the bucket.
 *
 * Closing this properly means deciding whether income reporting is
 * Phoenix-anchored or viewer-anchored — a business question about a money
 * surface, not a formatting one — and then moving BOTH halves together.
 */
const INCOME_BUCKET_ZONE = FALLBACK_TIMEZONE;

interface IncomeEventsListProps {
  events: LoanIncomeEventDto[];
}

// Pure display: a payment-channel int → a human label, falling back to the
// raw number so a new channel never renders blank.
const CHANNEL_LABEL: Record<number, string> = {
  1: "Direct from borrower",
  2: "Reimbursed from seller income",
};

function channelLabel(channel: number): string {
  return CHANNEL_LABEL[channel] ?? `Channel ${channel}`;
}

function commissionTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "paid":
    case "approved":
      return "ok";
    case "rejected":
    case "withdrawn":
    case "cancelled":
    case "canceled":
      return "danger";
    case "submitted":
    case "under_review":
      return "info";
    default:
      return "neutral";
  }
}

export function IncomeEventsList({ events }: IncomeEventsListProps) {
  if (events.length === 0) {
    return (
      <EmptyState
        title="No recognized income events"
        description="This loan has not recognized any income events in the selected range. Income posts as the loan's payments are serviced."
        variant="noResults"
      />
    );
  }

  return (
    <Card data-testid="income-events-list">
      <h2 className="m-0 mb-space-4 font-display text-h3 font-bold text-onyx">
        Income events
      </h2>
      <Table>
        <THead>
          <TR>
            <TH>Recognized</TH>
            <TH>Classification</TH>
            <TH>Revenue pool</TH>
            <TH>Channel</TH>
            <TH>Recipient</TH>
            <TH className="text-right">Expected</TH>
            <TH className="text-right">Received</TH>
            <TH className="text-right">Variance</TH>
          </TR>
        </THead>
        <TBody>
          {events.flatMap((event) => {
            const variance = event.expectedAmount - event.receivedAmount;
            const rows = [
              <TR key={event.id} data-testid={`income-event-row-${event.id}`}>
                <TD className="whitespace-nowrap text-gray-mid">
                  {formatDateInTz(event.recognizedAt, INCOME_BUCKET_ZONE)}
                </TD>
                <TD>{event.classification}</TD>
                <TD className="text-gray-mid">
                  {event.revenuePoolCode
                    ? event.revenuePoolName
                      ? `${event.revenuePoolCode} · ${event.revenuePoolName}`
                      : event.revenuePoolCode
                    : "—"}
                </TD>
                <TD className="text-gray-mid">{channelLabel(event.paymentChannel)}</TD>
                <TD className="text-gray-mid">{event.recipientCategory}</TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(event.expectedAmount)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(event.receivedAmount)}
                </TD>
                <TD
                  className={
                    Math.abs(variance) >= 0.005
                      ? "text-right tabular-nums text-status-error"
                      : "text-right tabular-nums text-gray-mid"
                  }
                >
                  {formatCurrency(variance)}
                </TD>
              </TR>,
            ];
            if (event.commissions.length > 0) {
              rows.push(
                <TR
                  key={`${event.id}-commissions`}
                  data-testid={`income-event-commissions-${event.id}`}
                >
                  <TD colSpan={8} className="bg-white-warm">
                    <div className="flex flex-col gap-space-1">
                      <span className="font-display text-caption font-semibold uppercase tracking-label text-gray-mid">
                        Generated commissions
                      </span>
                      <ul className="m-0 flex flex-col gap-space-1 p-0">
                        {event.commissions.map((c) => (
                          <li
                            key={c.id}
                            className="flex flex-wrap items-center gap-space-2 text-small"
                            data-testid={`income-event-commission-${c.id}`}
                          >
                            <span className="font-medium text-onyx">{c.claimNumber}</span>
                            {c.roleCode && (
                              <span className="text-gray-mid">{c.roleCode}</span>
                            )}
                            <span className="tabular-nums text-onyx">
                              {formatCurrency(c.commissionAmount)}
                            </span>
                            <StatusBadge tone={commissionTone(c.status)}>
                              {c.status}
                            </StatusBadge>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TD>
                </TR>,
              );
            }
            return rows;
          })}
        </TBody>
      </Table>
    </Card>
  );
}
